import { useCallback, useEffect, useMemo, useState } from "react";

export type BrowserNotificationPermission = NotificationPermission | "unsupported";
export type BrowserNotificationRequestOutcome = "resolved" | "timeout" | "error";

export type BrowserNotificationRequestResult = {
  permission: BrowserNotificationPermission;
  outcome: BrowserNotificationRequestOutcome;
};

const LAST_PROMPT_KEY = "etheria.notifications.lastPromptAt";
const ACCEPTED_KEY = "etheria.notifications.accepted";
const PROMPT_INTERVAL_MS = 60 * 60 * 1000;
const PERMISSION_REQUEST_TIMEOUT_MS = 10_000;

function hasNotificationSupport(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

export function readBrowserNotificationPermission(): BrowserNotificationPermission {
  if (!hasNotificationSupport()) return "unsupported";
  return Notification.permission;
}

function readLastPromptAt(): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(LAST_PROMPT_KEY);
    const parsed = raw ? Number.parseInt(raw, 10) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

export function rememberBrowserNotificationPromptSeen(now = Date.now()) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LAST_PROMPT_KEY, String(now));
  } catch {
    // La navigation privee peut refuser le stockage sans bloquer la permission native.
  }
}

export function markBrowserNotificationsAccepted() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ACCEPTED_KEY, "1");
  } catch {
    // La permission navigateur reste la source de verite.
  }
}

export function shouldShowBrowserNotificationPrompt(now = Date.now()): boolean {
  const permission = readBrowserNotificationPermission();
  if (permission === "unsupported" || permission === "granted") return false;
  return now - readLastPromptAt() >= PROMPT_INTERVAL_MS;
}

export function browserNotificationPromptDelay(now = Date.now()): number {
  const permission = readBrowserNotificationPermission();
  if (permission === "unsupported" || permission === "granted") return Number.POSITIVE_INFINITY;
  const remaining = PROMPT_INTERVAL_MS - (now - readLastPromptAt());
  return Math.max(0, remaining);
}

export function browserNotificationStatusLabel(
  permission: BrowserNotificationPermission,
  isSecureContext: boolean,
): string {
  if (permission === "unsupported") return "Non supporte par ce navigateur";
  if (!isSecureContext) return "Disponible uniquement en HTTPS";
  if (permission === "granted") return "Actives";
  if (permission === "denied") return "Bloquees dans le navigateur";
  return "A activer";
}

export function useBrowserNotifications() {
  const [permission, setPermission] = useState<BrowserNotificationPermission>(() => readBrowserNotificationPermission());

  const refresh = useCallback(() => {
    setPermission(readBrowserNotificationPermission());
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [refresh]);

  const requestPermission = useCallback(async (): Promise<BrowserNotificationRequestResult> => {
    if (!hasNotificationSupport() || !window.isSecureContext) {
      const next = readBrowserNotificationPermission();
      setPermission(next);
      return { permission: next, outcome: "error" };
    }

    rememberBrowserNotificationPromptSeen();

    let timeoutId: number | undefined;
    let outcome: BrowserNotificationRequestOutcome = "resolved";
    let next: BrowserNotificationPermission;

    try {
      const nativeRequest = Notification.requestPermission().then((value) => ({
        permission: value as BrowserNotificationPermission,
        outcome: "resolved" as const,
      }));
      const timeout = new Promise<BrowserNotificationRequestResult>((resolve) => {
        timeoutId = window.setTimeout(() => {
          resolve({ permission: readBrowserNotificationPermission(), outcome: "timeout" });
        }, PERMISSION_REQUEST_TIMEOUT_MS);
      });
      const result = await Promise.race([nativeRequest, timeout]);
      next = result.permission;
      outcome = result.outcome;
    } catch {
      next = readBrowserNotificationPermission();
      outcome = "error";
    } finally {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    }

    setPermission(next);
    if (next === "granted") markBrowserNotificationsAccepted();
    return { permission: next, outcome };
  }, []);

  const sendPreviewNotification = useCallback(() => {
    if (readBrowserNotificationPermission() !== "granted") return;
    try {
      new Notification("Etheria", {
        body: "Notifications activees. Les alertes importantes du royaume arriveront ici.",
        tag: "etheria-notifications-ready",
      });
    } catch {
      // Certains navigateurs refusent encore les notifications locales malgre la permission.
    }
  }, []);

  return useMemo(() => {
    const isSecureContext = typeof window !== "undefined" ? window.isSecureContext : false;
    return {
      permission,
      isSupported: permission !== "unsupported",
      isSecureContext,
      isGranted: permission === "granted",
      isDenied: permission === "denied",
      isDefault: permission === "default",
      statusLabel: browserNotificationStatusLabel(permission, isSecureContext),
      refresh,
      requestPermission,
      sendPreviewNotification,
    };
  }, [permission, refresh, requestPermission, sendPreviewNotification]);
}
